import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTheme } from '@mui/material';
import { Layer, Source, useMap } from 'shared/map-exports';
import { ON_TOP_OF_EVERYTHING_LAYER_ID } from 'features/mapping/styles/constants';
import { useQuery } from '@tanstack/react-query';
import { usePoisState } from 'state/usePoisState';
import { useSelectSingletonPoi } from 'features/mapping/hooks/useSelectSingletonPoi';
import { usePrevious } from 'hooks/usePrevious';
import { AircraftDetails } from 'features/mapping/mapping.types';
import { isEqual } from 'es-toolkit/compat';
import { hasValidCoordinates } from 'shared/utils';
import { useReleaseFlag } from 'hooks/useReleaseFlags';
import {
  aircraftsToGeoJson,
  firefightAirCraftsToObject,
  mergeDataToCache,
} from './utils';
import {
  fetchAircraftTraces,
  fetchAircraftsByHexCode,
  fetchFirefightingAircrafts,
  fetchActiveAircrafts,
} from './api';
import {
  ADSB_FETCH_INTERVAL_MS,
  DEFAULT_LAYER_TIMEOUT_MS,
  AIRCRAFT_LOCATION_REFRESH_INTERVAL_MS,
  AIRCRAFT_TRACE_REFRESH_INTERVAL_MS,
  TRACE_SOURCE_ID,
  TRACE_LAYER_ID,
} from './constants';
import {
  AircraftCache,
  AircraftCacheNoData,
  AircraftLayerFeature,
  FirefightingAircraft,
} from './types';
import { getAircraftLabelStyles, traceStyle } from './AircraftsLayer.styles';
import { AircraftIconMarkers } from './AircraftIconMarkers';

type AircraftsLayerProps = {
  visible: boolean;
};

const AIRCRAFT_LABELS_SOURCE_ID = 'aircraft-labels';
const AIRCRAFT_LABELS_LAYER_ID = 'aircraft-labels-layer';

export const AircraftsLayer = (
  props: AircraftsLayerProps,
): JSX.Element | null => {
  const { visible } = props;
  const { current: map } = useMap();
  const theme = useTheme();
  const useVendorCdn = useReleaseFlag('aircraft_layer_use_vendor_cdn');

  const { selectedPoi, setSelectedSingletonPoi, clearSelectedPois } =
    usePoisState();
  const selectSingletonPoi = useSelectSingletonPoi();
  const [hoveredAircraftHexCode, setHoveredAircraftHexCode] = useState<
    string | null
  >(null);
  const [selectedAircraftHexCode, setSelectedAircraftHexCode] = useState('');

  // Hex codes whose labels are always shown (regardless of zoom). Baked into
  // the label style so it regenerates on hover/selection change.
  const activeLabelHexCodes = useMemo(
    () =>
      [hoveredAircraftHexCode, selectedAircraftHexCode].filter(
        Boolean,
      ) as string[],
    [hoveredAircraftHexCode, selectedAircraftHexCode],
  );
  const aircraftLabelStyles = useMemo(
    () => getAircraftLabelStyles(theme, activeLabelHexCodes),
    [theme, activeLabelHexCodes],
  );

  // this acts as a local cache of the active aircrafts query- with us updating the values within it but keeping the reference
  // to planes that are no longer included in the ADSB response for periods of time.
  const [activePlanesCache, setActivePlanesCache] =
    useState<AircraftCacheNoData>({});

  // this acts as a local cache of the aircraft final data - with us updating the values within it but keeping the reference
  // to planes that are no longer included in the ADSB response for periods of time.
  const [planePositionCache, setPlanePositionCache] = useState<AircraftCache>(
    {},
  );

  const selectedAircraftPoi =
    selectedPoi?.type === 'aircraft' ? selectedPoi : null;
  const prevSelectedAircraftPoi = usePrevious(selectedAircraftPoi);

  useEffect(() => {
    if (prevSelectedAircraftPoi && !selectedAircraftPoi) {
      setSelectedAircraftHexCode('');
    }
  }, [prevSelectedAircraftPoi, selectedAircraftPoi]);

  const handleHover = useCallback((item: AircraftLayerFeature | null) => {
    setHoveredAircraftHexCode(item?.properties.hex || null);
  }, []);

  const handleClick = useCallback(
    (item: AircraftLayerFeature) => {
      if (!map) return;
      setSelectedAircraftHexCode(item.properties.hex);

      const aircraft = {
        name: item.properties.name,
        model: item.properties.model,
        tailNum: item.properties.tailNum,
        type: item.properties.type,
        hex: item.properties.hex,
        altitude: item.properties.altitude,
        speed: item.properties.speed,
        squawk: item.properties.squawk,
      };

      selectSingletonPoi({
        type: 'aircraft',
        id: aircraft.hex,
        metadata: aircraft,
      });
    },
    [map, selectSingletonPoi],
  );

  const dbQuery = useQuery({
    queryKey: ['firefighting-aircrafts', visible],
    queryFn: () => (visible ? fetchFirefightingAircrafts() : []),
    staleTime: DEFAULT_LAYER_TIMEOUT_MS,
  });

  const dbData = useMemo(() => dbQuery.data || [], [dbQuery.data]);

  const activeDataQuery = useQuery({
    queryKey: ['activeAircraft', useVendorCdn],
    queryFn: () => fetchActiveAircrafts(dbData, useVendorCdn),
    refetchInterval: ADSB_FETCH_INTERVAL_MS,
    // If visible and we have the data for aircrafts from our db fetched
    enabled: visible && Boolean(dbData.length),
  });
  const { data: activeData } = activeDataQuery;

  useEffect(() => {
    if (!activeData) return;
    setActivePlanesCache((cached) =>
      mergeDataToCache(activeData, cached, false),
    );
  }, [activeData]);

  const hexList = useMemo(
    () => Object.keys(activePlanesCache),
    [activePlanesCache],
  );
  const activeMetadata = useMemo(() => {
    if (!dbData.length || !hexList) return {};

    const dbAircrafts = firefightAirCraftsToObject(dbData);
    return hexList.reduce(
      (acc, hex) => {
        const aircraftMetadata = dbAircrafts[hex];
        if (aircraftMetadata) {
          acc[hex] = aircraftMetadata;
        }
        return acc;
      },
      {} as Record<string, FirefightingAircraft>,
    );
  }, [hexList, dbData]);

  const filteredAircraftsQuery = useQuery({
    queryKey: ['filteredAircrafts', useVendorCdn],
    queryFn: () => fetchAircraftsByHexCode(hexList.join(','), useVendorCdn),
    refetchInterval: AIRCRAFT_LOCATION_REFRESH_INTERVAL_MS,
    // If visible and we have active aircrafts hex codes
    enabled: visible && Boolean(hexList.length),
  });

  useEffect(() => {
    if (!filteredAircraftsQuery.data) {
      return;
    }
    setPlanePositionCache((cached) =>
      mergeDataToCache(filteredAircraftsQuery.data, cached, true),
    );
  }, [filteredAircraftsQuery.data]);

  // The active aircraft is whichever is hovered, or selected if none is hovered.
  const activeAircraftHexCode =
    hoveredAircraftHexCode || selectedAircraftHexCode;
  const aircraftTracesQuery = useQuery({
    queryKey: ['aircraftTraces', activeAircraftHexCode, useVendorCdn],
    queryFn: () => fetchAircraftTraces(activeAircraftHexCode, useVendorCdn),
    // We need to refetch the aircraft traces for the selected aircraft,
    // at the same interval as the filtered aircrafts query
    refetchInterval: AIRCRAFT_TRACE_REFRESH_INTERVAL_MS,
    enabled: Boolean(activeAircraftHexCode),
  });

  const sourceData = useMemo(() => {
    const aircraftPositions = Object.values(planePositionCache).map(
      (item) => item.data,
    );
    return aircraftsToGeoJson(
      aircraftPositions,
      activeMetadata,
      activeLabelHexCodes,
    );
  }, [planePositionCache, activeMetadata, activeLabelHexCodes]);

  const selectedAircraftPoiInSource = useMemo(() => {
    if (!selectedAircraftHexCode) return undefined;
    return sourceData.features.find(
      (feat) => feat.properties.hex === selectedAircraftHexCode,
    );
  }, [selectedAircraftHexCode, sourceData.features]);

  const activeAircraftInSource = useMemo(() => {
    if (!activeAircraftHexCode) return undefined;
    return sourceData.features.find(
      (feat) => feat.properties.hex === activeAircraftHexCode,
    );
  }, [activeAircraftHexCode, sourceData.features]);

  const currentAircraftDetails = useRef<AircraftDetails | null>(null);

  // This updates the point of interest popup with live altitude/speed data
  useEffect(() => {
    if (!selectedAircraftPoiInSource || !selectedAircraftPoi) return;
    const item = selectedAircraftPoiInSource;

    const aircraft: AircraftDetails = {
      name: item.properties.name,
      model: item.properties.model,
      tailNum: item.properties.tailNum,
      type: item.properties.type,
      hex: item.properties.hex,
      altitude: item.properties.altitude,
      speed: item.properties.speed,
      squawk: item.properties.squawk,
    };

    if (isEqual(aircraft, currentAircraftDetails.current)) return;

    currentAircraftDetails.current = aircraft;

    setSelectedSingletonPoi({
      type: 'aircraft',
      id: aircraft.hex,
      metadata: aircraft,
    });
  }, [
    selectedAircraftPoiInSource,
    selectedAircraftPoi,
    setSelectedSingletonPoi,
  ]);

  useEffect(() => {
    if (
      !selectedAircraftPoi ||
      prevSelectedAircraftPoi !== selectedAircraftPoi
    ) {
      return;
    }

    // Source data was re-fetch and selected poi is no longer included
    if (!selectedAircraftPoiInSource) {
      clearSelectedPois();
      setSelectedAircraftHexCode('');
    }
  }, [
    selectedAircraftPoiInSource,
    selectedAircraftPoi,
    clearSelectedPois,
    prevSelectedAircraftPoi,
  ]);

  const traceData = useMemo(() => {
    if (!aircraftTracesQuery.data || !activeAircraftHexCode) {
      return {
        type: 'Feature' as const,
        properties: {},
        geometry: {
          type: 'LineString' as const,
          coordinates: [],
        },
      };
    }

    const aircraftLat = activeAircraftInSource?.geometry?.coordinates[1] ?? 0;
    const aircraftLng = activeAircraftInSource?.geometry?.coordinates[0] ?? 0;

    const coordinates = (aircraftTracesQuery.data.trace ?? []).map(
      ([_, lat, lng]) => [lng, lat],
    );

    if (hasValidCoordinates({ lat: aircraftLat, lng: aircraftLng })) {
      coordinates.push([aircraftLng, aircraftLat]);
    }

    return {
      type: 'Feature' as const,
      properties: {},
      geometry: {
        type: 'LineString' as const,
        coordinates,
      },
    };
  }, [
    aircraftTracesQuery.data,
    activeAircraftHexCode,
    activeAircraftInSource?.geometry?.coordinates,
  ]);

  return (
    <>
      <Source
        id={TRACE_SOURCE_ID}
        type="geojson"
        data={traceData}
        maxzoom={12}
        lineMetrics
      >
        <Layer
          id={TRACE_LAYER_ID}
          beforeId={ON_TOP_OF_EVERYTHING_LAYER_ID}
          {...traceStyle}
        />
      </Source>

      <AircraftIconMarkers
        data={sourceData}
        onClick={handleClick}
        onHover={handleHover}
        visible={visible}
      />

      <Source id={AIRCRAFT_LABELS_SOURCE_ID} type="geojson" data={sourceData}>
        <Layer
          id={AIRCRAFT_LABELS_LAYER_ID}
          beforeId={ON_TOP_OF_EVERYTHING_LAYER_ID}
          {...aircraftLabelStyles}
        />
      </Source>
    </>
  );
};
